import { isNativeApprovalType } from '@/utils/approval/approvalAppTab';
import { isSaTravelType } from '@/utils/approval/saTravelExpense';
import type {
  ApprovalViewApprover,
  ApproverStatus,
  DocumentStatus,
} from './types';

/** 이미 처리해 더 손댈 게 없는 결재자 상태 */
const SETTLED_APPROVER_STATUSES: readonly ApproverStatus[] = ['C', 'R', 'X'];

/**
 * 지금 결재할 차례가 나인지. 협조결재는 같은 order 에 여러 명이 들어가므로 그 order 를
 * 전부 봐야 하고, 이미 처리한 뒤 남은 협조자를 기다리는 동안은 내 차례가 아님.
 *
 * 대기 상태는 '0'/'P' 가 섞여 오므로(개별 status 미갱신) 화이트리스트로 잡으면 안 됨.
 *
 * 보류 문서는 보류를 건 본인만 재처리 — 같은 order 의 다른 협조자는 대상이 아님
 * (레거시 approval_view_document.php 의 "보류 상태에서는 현재 결재자의 상태도 보류여야" 조건).
 * 단 개별 status 가 갱신 안 된 응답에서 보류자를 못 찾으면 아무도 처리 못 해 문서가 잠기므로,
 * 그 경우엔 order 전체를 대상으로 되돌림.
 */
export const isCurrentApproverMeOf = (
  approvers: ApprovalViewApprover[],
  currentApproverOrder: number | undefined,
  myPassportId: string,
  documentStatus: DocumentStatus,
): boolean => {
  if (!myPassportId) return false;

  const pending = approvers.filter(
    (a) =>
      a.order === currentApproverOrder &&
      !SETTLED_APPROVER_STATUSES.includes(a.status),
  );
  const holders =
    documentStatus === 'H' ? pending.filter((a) => a.status === 'H') : [];
  const candidates = holders.length > 0 ? holders : pending;

  return candidates.some((a) => a.passportId === myPassportId);
};

/**
 * status × 본인 여부 × 양식 조합으로 각 버튼 노출 여부 계산.
 * 액션 버튼 row(DocumentActions) 와 더보기 메뉴(ApprovalView) 가 함께 쓰는 단일 판정 — 조건은 여기에만 둘 것.
 */
export const computeActionVisibility = ({
  status,
  isAuthorMe,
  isCurrentApproverMe,
  apvType,
}: {
  status: DocumentStatus;
  isAuthorMe: boolean;
  isCurrentApproverMe: boolean;
  apvType: number;
}) => {
  // 편집·삭제 = status(S 또는 R) + 작성자 본인 + V2 작성폼이 있는 양식.
  // 조회전용 유형(근태 취소 품의 등)은 편집 진입 시 폼 preload 가 없어 스피너에 갇힘 — edit 페이지 404 게이트와 같은 기준
  const editable =
    (status === 'R' || status === 'S') &&
    isAuthorMe &&
    isNativeApprovalType(apvType);
  // SA 출장비 정산(177)·사전 신청(179)은 SAP 진입 값 없이 다시 열 수 없어 레거시부터 편집 숨김(삭제만)
  const showEdit = editable && !isSaTravelType(apvType);
  // 삭제 노출 = 편집과 동일(임시저장 문서도 삭제 허용)
  const showDelete = editable;
  // 임시저장(S)은 기안자 본인이 대상 — 레거시는 기안자=결재자 1번이라 status 조건만으로 걸렸지만
  // v2 는 기안자가 approvers 에 없어(AuthorCard 별도) isCurrentApproverMe 가 항상 false
  const showActionTrigger =
    (status === 'P' && isCurrentApproverMe) ||
    (status === 'H' && isCurrentApproverMe) ||
    (status === 'S' && isAuthorMe);
  return { showEdit, showDelete, showActionTrigger };
};
